import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { DevCenter } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { DevCenterClient } from "../devCenterClient";
import {
  Project,
  DevCenterListProjectsNextOptionalParams,
  DevCenterListProjectsOptionalParams,
  DevCenterListProjectsResponse,
  DevCenterGetProjectOptionalParams,
  DevCenterGetProjectResponse,
  DevCenterGetProjectAbilitiesOptionalParams,
  DevCenterGetProjectAbilitiesResponse,
  DevCenterListProjectsNextResponse,
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing DevCenter operations. */
export class DevCenterImpl implements DevCenter {
  private readonly client: DevCenterClient;

  /**
   * Initialize a new instance of the class DevCenter class.
   * @param client Reference to the service client
   */
  constructor(client: DevCenterClient) {
    this.client = client;
  }

  /**
   * Lists all projects.
   * @param options The options parameters.
   */
  public listProjects(
    options?: DevCenterListProjectsOptionalParams,
  ): PagedAsyncIterableIterator<Project> {
    const iter = this.listProjectsPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listProjectsPagingPage(options, settings);
      },
    };
  }

  private async *listProjectsPagingPage(
    options?: DevCenterListProjectsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Project[]> {
    let result: DevCenterListProjectsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listProjects(options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listProjectsNext(continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listProjectsPagingAll(
    options?: DevCenterListProjectsOptionalParams,
  ): AsyncIterableIterator<Project> {
    for await (const page of this.listProjectsPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all projects.
   * @param options The options parameters.
   */
  private _listProjects(
    options?: DevCenterListProjectsOptionalParams,
  ): Promise<DevCenterListProjectsResponse> {
    return this.client.sendOperationRequest(
      { options },
      listProjectsOperationSpec,
    );
  }

  /**
   * Gets a project.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  getProject(
    projectName: string,
    options?: DevCenterGetProjectOptionalParams,
  ): Promise<DevCenterGetProjectResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      getProjectOperationSpec,
    );
  }

  /**
   * Gets the signed-in user's permitted abilities in a project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId   The AAD object id of the user. If value is 'me', the identity is taken from the
   *                 authentication context.
   * @param options The options parameters.
   */
  getProjectAbilities(
    projectName: string,
    userId: string,
    options?: DevCenterGetProjectAbilitiesOptionalParams,
  ): Promise<DevCenterGetProjectAbilitiesResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, options },
      getProjectAbilitiesOperationSpec,
    );
  }

  /**
   * ListProjectsNext
   * @param nextLink The nextLink from the previous successful call to the ListProjects method.
   * @param options The options parameters.
   */
  private _listProjectsNext(
    nextLink: string,
    options?: DevCenterListProjectsNextOptionalParams,
  ): Promise<DevCenterListProjectsNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listProjectsNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listProjectsOperationSpec: coreClient.OperationSpec = {
  path: "/projects",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedProject,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevCenterListProjectsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  headerParameters: [Parameters.accept],
  serializer,
};
const getProjectOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Project,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevCenterGetProjectExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName],
  headerParameters: [Parameters.accept],
  serializer,
};
const getProjectAbilitiesOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/abilities",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ProjectAbilities,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevCenterGetProjectAbilitiesExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listProjectsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedProject,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevCenterListProjectsNextExceptionHeaders,
    },
  },
  urlParameters: [Parameters.endpoint, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
